import React, { useState } from 'react';
import { Container, Typography, Box, TextField, Button, Alert, Paper, FormControlLabel, CircularProgress, Switch } from '@mui/material';
import { formSettingsApi, usersApi } from '../services/api';

const Settings: React.FC = () => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [hideAfrDetails, setHideAfrDetails] = useState(false);
  const [hideReferentnaVrednost, setHideReferentnaVrednost] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Učitaj podešavanja sa backend-a na mount
  React.useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Master admin učitava globalna podešavanja da može da ih menja
        const res = user.role === 'master_admin' 
          ? await formSettingsApi.getGlobalFormSettings()
          : await formSettingsApi.getFormSettings();
        
        if (res) {
          setHideAfrDetails(res.hideAfrDetails || false);
          setHideReferentnaVrednost(res.hideReferentnaVrednost || false);
        } else {
          setHideAfrDetails(false);
          setHideReferentnaVrednost(false);
        }
      } catch (e) {
        setHideAfrDetails(false);
        setHideReferentnaVrednost(false);
      } finally {
        setLoadingSettings(false);
      }
    };
    fetchSettings();
      }, []);

  const handleChangePassword = async () => {
    setError(null);
    setSuccess(null);
    if (!oldPassword || !newPassword || !confirmPassword) {
      setError('Sva polja su obavezna.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Nove lozinke se ne poklapaju.');
      return;
    }
    setLoading(true);
    try {
      await usersApi.changePassword(oldPassword, newPassword);
      setSuccess('Lozinka uspešno promenjena.');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Greška pri promeni lozinke.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    setSaveSuccess(null);
    setSaveError(null);
    try {
      await formSettingsApi.saveFormSettings(hideAfrDetails, hideReferentnaVrednost);
      setSaveSuccess('Podešavanje je sačuvano.');
    } catch (e: any) {
      setSaveError(e?.response?.data?.error || 'Greška pri čuvanju podešavanja.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>Podešavanja profila</Typography>
        <Box sx={{ mb: 2 }}>
          <Typography><b>Korisničko ime:</b> {user.username}</Typography>
          <Typography><b>Uloga:</b> {user.role === 'master_admin' ? 'Master admin' : user.role === 'admin' ? 'Administrator' : user.role === 'direktor' ? 'Direktor' : user.role === 'tehnolog' ? 'Tehnolog' : user.role === 'kontrolor' ? 'Kontrolor' : 'Korisnik'}</Typography>
        </Box>
        {user.role === 'master_admin' && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>Podešavanja forme</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ova podešavanja važe za sve korisnike uključujući administratore, direktore i kontrolore.
              <br />
              <strong>Napomena:</strong> Promene se primenjuju automatski za sve korisnike kroz nekoliko sekundi.
            </Typography>
            {loadingSettings ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <span>Učitavanje podešavanja...</span>
              </Box>
            ) : (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={hideAfrDetails}
                      onChange={e => setHideAfrDetails(e.target.checked)}
                      color="primary"
                      disabled={user.role !== 'master_admin'}
                    />
                  }
                  label="Sakrij AFr detaljna polja (d, R, A, Rs, r) u formi i šablonu"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={hideReferentnaVrednost}
                      onChange={e => setHideReferentnaVrednost(e.target.checked)}
                      color="primary"
                      disabled={user.role !== 'master_admin'}
                    />
                  }
                  label="Sakrij Referentna vrednost sekciju u formi"
                />
                <Button variant="contained" sx={{ ml: 2 }} onClick={handleSaveSettings} disabled={saving}>
                  Sačuvaj
                </Button>
                {saveSuccess && <Alert severity="success" sx={{ mt: 2 }}>{saveSuccess}</Alert>}
                {saveError && <Alert severity="error" sx={{ mt: 2 }}>{saveError}</Alert>}
              </>
            )}
          </Box>
        )}

        <Typography variant="h6" gutterBottom>Promena lozinke</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        <TextField
          label="Stara lozinka"
          type="password"
          value={oldPassword}
          onChange={e => setOldPassword(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        />
        <TextField
          label="Nova lozinka"
          type="password"
          value={newPassword}
          onChange={e => setNewPassword(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        />
        <TextField
          label="Potvrda nove lozinke"
          type="password"
          value={confirmPassword}
          onChange={e => setConfirmPassword(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        />
        <Button variant="contained" onClick={handleChangePassword} disabled={loading}>
          Promeni lozinku
        </Button>
      </Paper>
    </Container>
  );
};

export default Settings; 